import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Checkbox,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Slider,
  Toggle,
} from '@statsbomb/kitbag-components';
import { useReactiveVar } from '@apollo/client';
import classNames from 'classnames';
import uiDefault from '../../apollo/uiDefault';
import { returnReadableYardsLabel } from '../../utils/helpers/general';
import { convertIntToNth } from '../../utils/helpers/formatting';
import { enableFilter } from './Filters.helper';
import {
  uiState,
  cf_Down,
  cf_DownEnabled,
  cf_Distance,
  cf_DistanceEnabled,
} from '../../apollo';
import {
  FilterRoundel,
  FiltersHR,
  TriggerButtonContainer,
  ModalWidth,
  FilterDynamicValue,
} from './ContextFilters.styles';
import { useIsEligiblePage } from './ContextFilters.hooks';
import { FormField, FormGroupEqual } from '../../styles/global.styles';

const DistanceFilter = ({ handleEnabledToggle }) => {
  // modal open/close
  const [st_DistanceOpen, setDistanceOpen] = useState(false);
  // context filters in global state
  const cf_DownRV = useReactiveVar(cf_Down);
  const cf_DownEnabledRV = useReactiveVar(cf_DownEnabled);
  const cf_DistanceRV = useReactiveVar(cf_Distance);
  const cf_DistanceEnabledRV = useReactiveVar(cf_DistanceEnabled);

  // context filters in local state
  const [st_Down, setDown] = useState(cf_DownRV);
  const [st_Distance, setDistance] = useState(cf_DistanceRV);

  const handleDown = (isDownChecked, index) => {
    const downs = [...st_Down];
    downs[index] = isDownChecked;
    setDown([...downs]);
    enableFilter(cf_DownEnabledRV, cf_DownEnabled, 'down');
  };

  const handleDistance = (evt, sliderValues) => {
    setDistance([...sliderValues]);
  };

  const handleModalClose = () => {
    const { contextFilters } = uiState();
    const { down, distance } = contextFilters;

    cf_Down([...st_Down]);
    down.value = [...st_Down];
    cf_DownEnabled(cf_DownEnabledRV);

    cf_Distance([...st_Distance]);
    distance.value = [...st_Distance];
    cf_DistanceEnabled(cf_DistanceEnabledRV);

    sessionStorage.setItem('uiStateLocal', JSON.stringify(uiState()));
    setDistanceOpen(false);
  };

  const handleReset = () => {
    const { down, distance } = uiDefault.contextFilters;

    setDown([...down.value]);
    cf_DownEnabled(down.enabled);
    setDistance([...distance.value]);
    cf_DistanceEnabled(distance.enabled);

    uiState().contextFilters.down.value = [...down.value];
    uiState().contextFilters.down.enabled = down.enabled;
    uiState().contextFilters.distance.value = [...distance.value];
    uiState().contextFilters.distance.enabled = distance.enabled;

    sessionStorage.setItem('uiStateLocal', JSON.stringify(uiState()));
  };

  const filterCount = [cf_DownEnabledRV, cf_DistanceEnabledRV].filter(
    Boolean
  ).length;
  const isEligiblePage = useIsEligiblePage('DOWN_AND_DISTANCE');
  const isEnabled = filterCount > 0 && isEligiblePage;

  return (
    <>
      <TriggerButtonContainer>
        <Button
          variant={isEnabled ? 'primary' : 'secondary'}
          size="small"
          isDisabled={!isEligiblePage}
          onClick={() => setDistanceOpen(true)}
        >
          Down & Distance
        </Button>
        {isEnabled && <FilterRoundel>{filterCount}</FilterRoundel>}
      </TriggerButtonContainer>

      <Modal onCancel={handleModalClose} isOpen={st_DistanceOpen}>
        <ModalWidth $minWidth="570px">
          <ModalHeader>Down and Distance</ModalHeader>
          <ModalBody>
            <FormField $marginBottom="0.5rem">
              <Toggle
                id="down-cf-toggle"
                label="Down"
                onChange={() =>
                  handleEnabledToggle(
                    !cf_DownEnabledRV,
                    cf_DownEnabled,
                    'down',
                    {
                      enabled: !cf_DownEnabledRV,
                    }
                  )
                }
                checked={cf_DownEnabledRV}
                size="small"
                data-testid={123}
              />
            </FormField>
            <FormGroupEqual>
              {st_Down &&
                st_Down.map((down, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={`down-field-${index}`}>
                    <Checkbox
                      id={`down-field-${index}-checkbox`}
                      label={index < 4 ? convertIntToNth(index + 1) : 'Extra'}
                      onChange={() => handleDown(!down, index)}
                      value={String(st_Down[index])}
                      checked={down}
                      size="small"
                      disabled={!cf_DownEnabledRV}
                    />
                  </div>
                ))}
            </FormGroupEqual>
            <FiltersHR $margin="1.25rem 1rem 0.75rem" />
            <FormField $marginBottom="0.5rem">
              <Toggle
                id="distance-to-go-cf-toggle"
                label="Distance"
                onChange={() =>
                  handleEnabledToggle(
                    !cf_DistanceEnabledRV,
                    cf_DistanceEnabled,
                    'distance',
                    {
                      enabled: !cf_DistanceEnabledRV,
                    }
                  )
                }
                checked={cf_DistanceEnabledRV}
                size="small"
              />
              <FilterDynamicValue
                className={classNames({
                  grey: !cf_DistanceEnabledRV,
                })}
              >
                {st_Distance && returnReadableYardsLabel(st_Distance, 0, 21)}
              </FilterDynamicValue>
            </FormField>
            <FormField $marginBottom="0.5rem">
              <Slider
                id="distance-context-filter-slider"
                // eslint-disable-next-line react/jsx-no-duplicate-props
                labels={Array(21)
                  .fill()
                  .map((_, index) => ({
                    hasNotch: true,
                    label: `${index}`,
                    position: index,
                  }))}
                min={0}
                max={21}
                onChange={handleDistance}
                presets={[
                  {
                    name: 'Short',
                    value: [0, 2],
                  },
                  {
                    name: 'Medium',
                    value: [2, 7],
                  },
                  {
                    name: 'Long',
                    value: [7, 21],
                  },
                ]}
                isDisabled={!cf_DistanceEnabledRV}
                isPushable
                unit="yds"
                value={[...st_Distance]}
                version={2}
              />
            </FormField>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={handleReset}>
              Reset
            </Button>
            <Button onClick={() => handleModalClose(setDistanceOpen)}>
              Save
            </Button>
          </ModalFooter>
        </ModalWidth>
      </Modal>
    </>
  );
};

DistanceFilter.propTypes = {
  handleEnabledToggle: PropTypes.func.isRequired,
};

export default DistanceFilter;
